import { Entity, Group, Thread, User } from '../types';

export const DefaultAppViewPropertyId = 'global';

export const NoAppViewPropertyValueOnServer = '';

export enum AppViewPropertyKey {
  AnalyticsTeachingBubble = 'user.storyline.analyticsTeachingBubble',
  CMSubscriptionTip = 'group.actionTips.CMSubscriptionTip',
  CMSubscriptionTeachingBubble = 'group.teachingBubbles.CMSubscriptionTeachingBubble',
  GroupAvatarTeachingBubble = 'group.teachingBubbles.GroupAvatarTeachingBubble',
  GroupCoverPhotoTeachingBubble = 'group.teachingBubbles.GroupCoverPhotoTeachingBubble',
  GroupDescriptionTeachingBubble = 'group.teachingBubbles.GroupDescriptionTeachingBubble',
  LikeNudge = 'user.actionTips.likeNudge',
  NetworkQuestionsOnboardingTip = 'networkQuestions.actionTips.onboarding',
  OutlookPublisherGuideOpen = 'user.storyline.outlookPublisherGuideOpen',
  SearchFRETeachingBubbleOpen = 'user.actionTips.FRETeachingBubbleStatus',
  StorylineCongratulationsTeachingBubbleStatus = 'user.storyline.congratulationsTeachingBubbleStatus',
  StorylinePageVisited = 'user.storyline.storylinePageHasBeenVisited',
  StorylineSuggestedMediaCoachingTip = 'user.storyline.suggestedMediaCoachingTip',
  StorylineSuggestedMentionsCoachingTip = 'user.storyline.suggestedMentionsCoachingTip',
  StorylineSuggestedReactOrReplyCoachingTip = 'user.storyline.suggestedReactOrReplyCoachingTip',
  CMGroupAdminStepper = 'group.actionTips.CMGroupAdminStepper',
}
export interface DismissableAppViewProperty {
  readonly isDismissed: boolean;
}
export type StorylineCoachingTipTypes =
  | AppViewPropertyKey.StorylineSuggestedMediaCoachingTip
  | AppViewPropertyKey.StorylineSuggestedMentionsCoachingTip
  | AppViewPropertyKey.StorylineSuggestedReactOrReplyCoachingTip;

export type StorylinePublisherCoachingTipTypes =
  | AppViewPropertyKey.StorylineSuggestedMediaCoachingTip
  | AppViewPropertyKey.StorylineSuggestedMentionsCoachingTip;

export interface LikeNudgeAppViewProperty {
  readonly lastDisplayed: number;
}

export interface StorylinePageVisitedAppViewProperty {
  readonly visited: boolean;
}

export interface OutlookPublisherGuideOpenAppViewProperty {
  readonly isOpen: boolean;
}

export interface SearchFRETeachingBubbleOpenAppViewProperty {
  readonly isOpen: boolean;
}

export interface StorylineCoachingTipAppViewProperty {
  readonly lastDisplayed?: number;
}

export interface AnalyticsTeachingBubbleAppViewProperty {
  readonly isVisited: boolean;
}

export enum NetworkQuestionsOnboardingTipStatus {
  Expanded = 'Expanded',
  Collapsed = 'Collapsed',
  Dismissed = 'Dismissed',
}

export interface NetworkQuestionsOnboardingTipAppViewProperty {
  readonly status: NetworkQuestionsOnboardingTipStatus;
  readonly shouldShowUpvoteBubble: boolean;
}

export interface StorylineThreadCoachingTipAppViewProperty extends StorylineCoachingTipAppViewProperty {
  readonly isDisplay?: boolean;
  readonly visibleThreadId?: Thread['id'];
  readonly visiblePageLocation?: string;
}

export enum StorylineCongratulationsTeachingBubbleStatus {
  ReadyToShow = 'ReadyToShow',
  Showing = 'Showing',
  Hide = 'Hide',
}
export interface StorylineCongratulationsTeachingBubbleStatusAppViewProperty {
  readonly status: StorylineCongratulationsTeachingBubbleStatus;
}

export interface SubscriptionTipAppViewProperty {
  readonly isDismissed: boolean;
  readonly remindMeLater: boolean;
  readonly remindMeLaterClickedDate: number | null;
}

export interface SubscriptionTeachingBubbleAppViewProperty {
  readonly isDismissed: boolean;
}

export type GroupAdminStepperAppViewProperty = DismissableAppViewProperty;

export interface GroupTeachingBubbleAppViewProperty {
  readonly isDismissed: boolean;
  readonly date: number | null;
}

export type AppViewPropertyJSON =
  | AnalyticsTeachingBubbleAppViewProperty
  | LikeNudgeAppViewProperty
  | NetworkQuestionsOnboardingTipAppViewProperty
  | OutlookPublisherGuideOpenAppViewProperty
  | SearchFRETeachingBubbleOpenAppViewProperty
  | SubscriptionTipAppViewProperty
  | SubscriptionTeachingBubbleAppViewProperty
  | GroupAdminStepperAppViewProperty
  | GroupTeachingBubbleAppViewProperty
  | StorylinePageVisitedAppViewProperty
  | StorylineCongratulationsTeachingBubbleStatusAppViewProperty
  | StorylineCoachingTipAppViewProperty
  | StorylineThreadCoachingTipAppViewProperty;

export type AppViewPropertyValue = AppViewPropertyJSON | typeof NoAppViewPropertyValueOnServer | null | undefined;
export interface AppViewProperty extends Entity {
  readonly id: Group['id'] | User['id'] | typeof DefaultAppViewPropertyId;
  readonly key: AppViewPropertyKey;
  readonly value: AppViewPropertyValue;
}

export type AppViewPropertiesEntityMap = Record<string, AppViewProperty>;

export type AppViewProperties = Partial<Record<AppViewPropertyKey, AppViewPropertiesEntityMap>>;

export const defaultSubscriptionTipAppViewProperty: SubscriptionTipAppViewProperty = {
  isDismissed: false,
  remindMeLater: false,
  remindMeLaterClickedDate: null,
};

export const defaultSubscriptionTeachingBubbleProperty: SubscriptionTeachingBubbleAppViewProperty = {
  isDismissed: false,
};

export const defaultGroupAdminStepperProperty: GroupAdminStepperAppViewProperty = {
  isDismissed: false,
};

export const defaultGroupTeachingBubbleAppViewProperty: GroupTeachingBubbleAppViewProperty = {
  isDismissed: false,
  date: null,
};

export const defaultNetworkQuestionsOnboardingTipAppViewProperty: NetworkQuestionsOnboardingTipAppViewProperty = {
  status: NetworkQuestionsOnboardingTipStatus.Expanded,
  shouldShowUpvoteBubble: false,
};

export const defaultLikeNudgeAppViewProperty: LikeNudgeAppViewProperty = { lastDisplayed: 0 };

const defaultStorylinePageVisitedProperty: StorylinePageVisitedAppViewProperty = { visited: true };
const defaultAnalyticsTeachingBubbleAppViewProperty: AnalyticsTeachingBubbleAppViewProperty = { isVisited: false };
const defaultOutlookPublisherGuideOpen: OutlookPublisherGuideOpenAppViewProperty = { isOpen: true };
const defaultSearchFRETeachingBubbleOpen: SearchFRETeachingBubbleOpenAppViewProperty = { isOpen: true };
const defaultStorylineCongratulationsTeachingBubbleProperty: StorylineCongratulationsTeachingBubbleStatusAppViewProperty =
  {
    status: StorylineCongratulationsTeachingBubbleStatus.Hide,
  };

const defaultStorylineSuggestedCoachingTip: StorylineCoachingTipAppViewProperty = { lastDisplayed: 0 };

export const defaultAppViewProperties: Record<AppViewPropertyKey, AppViewPropertyJSON> = {
  [AppViewPropertyKey.AnalyticsTeachingBubble]: defaultAnalyticsTeachingBubbleAppViewProperty,
  [AppViewPropertyKey.CMSubscriptionTip]: defaultSubscriptionTipAppViewProperty,
  [AppViewPropertyKey.CMSubscriptionTeachingBubble]: defaultSubscriptionTeachingBubbleProperty,
  [AppViewPropertyKey.GroupAvatarTeachingBubble]: defaultGroupTeachingBubbleAppViewProperty,
  [AppViewPropertyKey.GroupCoverPhotoTeachingBubble]: defaultGroupTeachingBubbleAppViewProperty,
  [AppViewPropertyKey.GroupDescriptionTeachingBubble]: defaultGroupTeachingBubbleAppViewProperty,
  [AppViewPropertyKey.LikeNudge]: defaultLikeNudgeAppViewProperty,
  [AppViewPropertyKey.NetworkQuestionsOnboardingTip]: defaultNetworkQuestionsOnboardingTipAppViewProperty,
  [AppViewPropertyKey.OutlookPublisherGuideOpen]: defaultOutlookPublisherGuideOpen,
  [AppViewPropertyKey.SearchFRETeachingBubbleOpen]: defaultSearchFRETeachingBubbleOpen,
  [AppViewPropertyKey.StorylinePageVisited]: defaultStorylinePageVisitedProperty,
  [AppViewPropertyKey.StorylineCongratulationsTeachingBubbleStatus]:
    defaultStorylineCongratulationsTeachingBubbleProperty,
  [AppViewPropertyKey.StorylineSuggestedMediaCoachingTip]: defaultStorylineSuggestedCoachingTip,
  [AppViewPropertyKey.StorylineSuggestedMentionsCoachingTip]: defaultStorylineSuggestedCoachingTip,
  [AppViewPropertyKey.StorylineSuggestedReactOrReplyCoachingTip]: defaultStorylineSuggestedCoachingTip,
  [AppViewPropertyKey.CMGroupAdminStepper]: defaultGroupAdminStepperProperty,
};
